// src/lib/publicReviews.ts
import { SUPABASE_URL } from '../services/supabaseClient';

export type PublicExperienceReview = {
  id: string;
  rating: number;
  review_title?: string | null;
  comment: string;
  customer_display_name: string;
  source_type?: string | null;
  source_label: string;
  voucher_code?: string | null;
  business_id?: string | null;
  business_name?: string | null;
  business_slug?: string | null;
  business_region?: string | null;
  partner_response?: string | null;
  partner_response_at?: string | null;
  featured?: boolean;
  language?: string | null;
  created_at?: string | null;
};

export type PublicReviewSummary = {
  count: number;
  average_rating: number;
  distribution: Record<'1' | '2' | '3' | '4' | '5', number>;
  latest_at?: string | null;
};

export type PublicReviewsResponse = {
  ok: boolean;
  reviews: PublicExperienceReview[];
  summary: PublicReviewSummary;
  error?: string;
  details?: string;
};

export type PublicReviewsQuery = {
  limit?: number;
  businessId?: string | null;
  businessSlug?: string | null;
  region?: string | null;
  sourceType?: string | null;
  minRating?: number | null;
  featuredOnly?: boolean;
};

const emptySummary: PublicReviewSummary = {
  count: 0,
  average_rating: 0,
  distribution: { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 },
  latest_at: null,
};

const getAnonKey = () => (import.meta as any)?.env?.VITE_SUPABASE_ANON_KEY || '';

export async function fetchPublicExperienceReviews(query: PublicReviewsQuery = {}): Promise<PublicReviewsResponse> {
  if (!SUPABASE_URL) {
    return { ok: false, reviews: [], summary: emptySummary, error: 'missing_supabase_url' };
  }

  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/public-experience-reviews`, {
      method: 'POST',
      headers: {
        apikey: getAnonKey(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'list',
        limit: query.limit ?? 12,
        business_id: query.businessId || undefined,
        business_slug: query.businessSlug || undefined,
        region: query.region || undefined,
        source_type: query.sourceType || undefined,
        min_rating: query.minRating || undefined,
        featured_only: Boolean(query.featuredOnly),
      }),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.ok === false) {
      return {
        ok: false,
        reviews: [],
        summary: emptySummary,
        error: payload?.error || 'public_reviews_unavailable',
        details: payload?.details || `HTTP ${response.status}`,
      };
    }

    return {
      ok: true,
      reviews: Array.isArray(payload?.reviews) ? payload.reviews : [],
      summary: payload?.summary || emptySummary,
    };
  } catch (error: any) {
    return {
      ok: false,
      reviews: [],
      summary: emptySummary,
      error: 'network_error',
      details: error?.message || String(error),
    };
  }
}
